/**
 * Graph explorer (spec §15.8): unbound SPARQL editor (persisted per workspace in IndexedDB),
 * templates, graph/table views, inspector, expansion, grouping, layouts, "Save as block".
 */
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Play } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { reconcileBuilderState } from "./builder/state";
import { useMe, usePages, usePrefixes, useStaleGraphs, type QueryResult } from "../api/hooks";
import { createBlock } from "../blocks/model";
import { openDoc } from "../sync/docManager";
import { getMeta, setMeta } from "../sync/persistence";
import { Button } from "../ui/Button";
import type { LayoutName } from "./CytoscapeView";
import { GraphCanvas } from "./GraphCanvas";
import { Inspector } from "./Inspector";
import { ResultTable } from "./ResultTable";
import { mountSparqlEditor, type SparqlEditorHandle } from "./SparqlEditor";
import { CANONICAL, TEMPLATES } from "./templates";
import { queryCacheKey, runWithCache } from "./queryCache";
import { useGraphExploration } from "./useGraphExploration";
import { useT } from "../i18n";

// [M34] Sparnatural and its dependencies load only when the builder first opens (rule 6)
const QueryBuilder = lazy(() => import("./builder/QueryBuilder").then((m) => ({ default: m.QueryBuilder })));

export function GraphExplorerPage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const t = useT();
  const me = useMe();
  const pages = usePages(workspaceId);
  const prefixes = usePrefixes();
  const prefixesRef = useRef<Record<string, string>>({});
  const host = useRef<HTMLDivElement>(null);
  const editor = useRef<SparqlEditorHandle | null>(null);
  const [initial, setInitial] = useState<string | null>(null);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [status, setStatus] = useState<{ running: boolean; error?: string; stale?: number }>({ running: false });
  const [opts, setOpts] = useState({ showTypeEdges: false, mergeDerived: true, groupByContainer: false });
  const [layout, setLayout] = useState<LayoutName>("fcose");
  const [view, setView] = useState<"graph" | "table">("graph");
  // [M34] the visual query builder, opened above the editor (spec §15.10)
  const [building, setBuilding] = useState(false);
  const builderKey = `ws:${workspaceId}`;
  const [selected, setSelected] = useState<string | null>(null);
  const [savePage, setSavePage] = useState("");
  const navigate = useNavigate();
  const storageKey = `explorer-query:${workspaceId}`;

  useEffect(() => {
    prefixesRef.current = prefixes.data ?? {};
  }, [prefixes.data]);

  useEffect(() => {
    void getMeta<string>(storageKey)
      .then((q) => setInitial(q ?? CANONICAL))
      .catch(() => setInitial(CANONICAL));
  }, [storageKey]);

  const run = useCallback(
    async (query: string) => {
      setStatus({ running: true });
      setSelected(null);
      try {
        const r = await runWithCache(workspaceId, query, await queryCacheKey("explorer", workspaceId, query));
        setResult(r.result);
        setStatus({ running: false, stale: r.stale ? r.fetchedAt : undefined, error: r.error });
      } catch (e) {
        setStatus({ running: false, error: e instanceof Error ? e.message : String(e) });
      }
    },
    [workspaceId],
  );
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  }, [run]);

  useEffect(() => {
    const el = host.current;
    if (initial === null || !el) return;
    let saveTimer: ReturnType<typeof setTimeout> | undefined;
    const h = mountSparqlEditor(el, {
      initial,
      workspaceId,
      prefixes: () => prefixesRef.current,
      onRun: (q) => void runRef.current(q),
      onChange: (v) => {
        clearTimeout(saveTimer);
        saveTimer = setTimeout(() => void setMeta(storageKey, v), 400);
      },
    });
    editor.current = h;
    return () => {
      clearTimeout(saveTimer);
      h.destroy();
      editor.current = null;
    };
  }, [initial, workspaceId, storageKey]);

  const staleGraphs = useStaleGraphs(workspaceId);
  const { graph, info, parents, expand, incremental } = useGraphExploration(workspaceId, result, { ...opts, staleGraphs: staleGraphs.data });
  const selectedNode = useMemo(() => graph?.nodes.find((n) => n.id === selected) ?? null, [graph, selected]);
  const showTable = view === "table" || !graph || graph.tableOnly || graph.tooLarge;

  const setQuery = (q: string) => {
    const v = editor.current?.view;
    if (!v) return;
    v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: q } });
  };

  const saveAsBlock = async () => {
    if (!savePage || !me.data || !editor.current) return;
    const q = editor.current.value();
    const handle = await openDoc("page", savePage);
    const treeId = createBlock(handle.doc, "graph_view", me.data.id, undefined, undefined, { title: t("graph.blockTitle"), query: q });
    void navigate({ to: "/w/$workspaceId/d/$docId", params: { workspaceId, docId: savePage }, hash: `block=${treeId}` });
    setTimeout(() => handle.release(), 5000); // the page editor holds its own reference by then
  };

  return (
    <div className="flex h-full min-h-0">
      <aside className="w-60 shrink-0 overflow-auto border-r border-neutral-200 p-3 text-sm dark:border-neutral-800">
        <Link to="/w/$workspaceId" params={{ workspaceId }} className="text-xs text-accent underline">{t("page.backToWorkspace")}</Link>
        <h2 className="mb-2 mt-3 font-semibold">{t("graph.templates")}</h2>
        <ul className="space-y-1">
          {TEMPLATES.map((tpl) => (
            <li key={tpl.id}>
              <button
                className="text-left text-xs underline"
                data-testid={`template-${tpl.id}`}
                onClick={() => {
                  const sel = selected ?? undefined;
                  setQuery(tpl.query({ block: sel?.startsWith("urn:km:blk:") ? sel : undefined, doc: sel?.startsWith("urn:km:doc:") ? sel.slice(11) : undefined }));
                }}
              >
                {t(tpl.labelKey)}
              </button>
            </li>
          ))}
        </ul>
        <h2 className="mb-1 mt-4 font-semibold">{t("graph.saveAsBlock")}</h2>
        <select className="w-full kss-control kss-field bg-transparent px-2 py-1 text-xs" value={savePage} onChange={(e) => setSavePage(e.target.value)} data-testid="save-page">
          <option value="">{t("graph.choosePage")}</option>
          {(pages.data ?? []).filter((p) => p.my_mode === "write").map((p) => (
            <option key={p.id} value={p.id}>{p.title || t("page.untitled")}</option>
          ))}
        </select>
        <Button className="mt-1" disabled={!savePage} onClick={() => void saveAsBlock()} data-testid="save-as-block">{t("common.save")}</Button>
      </aside>

      <div className="flex min-w-0 grow flex-col">
        <div className="border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2 px-3 pt-1.5 text-xs">
            <button
              className={building ? "font-medium text-accent underline" : "underline"}
              aria-expanded={building}
              onClick={() => {
                // a hand edit since the builder last wrote the query detaches it (Q95)
                if (!building) reconcileBuilderState(builderKey, editor.current?.value() ?? "");
                setBuilding(!building);
              }}
              data-testid="builder-toggle"
            >
              {t("graph.builder.open")}
            </button>
          </div>
          {building && (
            <Suspense fallback={null}>
              <QueryBuilder
                workspaceId={workspaceId}
                stateKey={builderKey}
                onUse={(q) => {
                  setQuery(q);
                  setBuilding(false);
                }}
              />
            </Suspense>
          )}
          <div ref={host} className="max-h-56 overflow-auto" data-testid="sparql-editor" />
          <div className="flex flex-wrap items-center gap-3 px-3 py-1.5 text-xs">
            <Button onClick={() => editor.current && void run(editor.current.value())} disabled={status.running} data-testid="run-query">
              <Play size={12} /> {t("graph.run")}
            </Button>
            <button className="underline" onClick={() => editor.current?.insertPrefixes()} data-testid="insert-prefixes">{t("graph.insertPrefixes")}</button>
            <span className="text-neutral-400">{t("graph.runHint")}</span>
            <label className="flex items-center gap-1"><input type="checkbox" checked={opts.showTypeEdges} onChange={(e) => setOpts({ ...opts, showTypeEdges: e.target.checked })} /> {t("graph.typeEdges")}</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={opts.groupByContainer} onChange={(e) => setOpts({ ...opts, groupByContainer: e.target.checked })} data-testid="group-by-container" /> {t("graph.groupByPage")}</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={opts.mergeDerived} onChange={(e) => setOpts({ ...opts, mergeDerived: e.target.checked })} /> {t("graph.hideDerived")}</label>
            <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={layout} onChange={(e) => setLayout(e.target.value as LayoutName)} data-testid="layout">
              <option value="fcose">fcose</option>
              <option value="elk-layered">{t("graph.layout.elk")}</option>
            </select>
            <button className="underline" onClick={() => setView(view === "graph" ? "table" : "graph")}>{view === "graph" ? t("graph.tableView") : t("graph.graphView")}</button>
            <span className="grow" />
            {status.running && <span>{t("graph.running")}</span>}
            {result && !status.running && (
              <span data-testid="query-status">
                {t("graph.rows", { count: result.rowCount })} · {result.elapsedMs} ms{result.truncated ? ` · ${t("graph.truncated")}` : ""}
                {graph && !graph.tableOnly ? ` · ${t("graph.nodes", { count: graph.nodes.length })}` : ""}
              </span>
            )}
            {status.stale && <span className="rounded bg-amber-100 px-1 text-amber-900" data-testid="graph-stale">{t("graph.staleSince", { time: new Date(status.stale).toLocaleTimeString() })}</span>}
            {status.error && !status.stale && <span className="text-red-600" data-testid="query-error">{status.error.slice(0, 200)}</span>}
            {graph?.tooLarge && <span className="text-amber-700">{t("graph.tooLarge")}</span>}
          </div>
        </div>
        <div className="flex min-h-0 grow">
          <div className="min-w-0 grow">
            {result && showTable ? (
              <ResultTable result={result} />
            ) : (
              <GraphCanvas graph={graph} info={info} layout={layout} parents={parents} incremental={incremental} onSelect={setSelected} onExpand={(id) => void expand(id)} />
            )}
          </div>
          <aside className="w-72 shrink-0 overflow-auto border-l border-neutral-200 dark:border-neutral-800">
            <Inspector workspaceId={workspaceId} node={selectedNode} info={selected ? info.get(selected) : undefined} edges={graph?.edges ?? []} onExpand={(id) => void expand(id)} />
          </aside>
        </div>
      </div>
    </div>
  );
}
